/**
 * Quality presets — the single knob mobile rendering is tuned through.
 *
 * `auto` picks `low | medium | high` from device hints (see device.ts).
 * `low` exists specifically so integrated mobile GPUs can run the painterly
 * pipeline without dropping bloom-driven readability cues entirely (the
 * Fresnel rim glow stays on at every level).
 *
 * The renderer does not import this module's enum directly; it consumes a
 * resolved `QualityProfile` so that future overrides (e.g. a debug menu
 * forcing a single knob) compose cleanly.
 *
 * Routing of user actions through the codebase:
 *
 *   client/src/main.ts (boot)
 *     -> resolveQualityProfile(detectQualityLevel())
 *        -> render/Renderer.ts                  (consumes the profile)
 *   settings UI (later milestones)
 *     -> setQualityLevel(level)
 *        -> resolveQualityProfile(level)
 *        -> render/Renderer.ts.applyProfile()   (hot-swappable)
 */

import { isLikelyMobileGpu, isTouchPrimary } from "../util/device.js";

export type QualityLevel = "low" | "medium" | "high";

export interface QualityProfile {
	bloom: "off" | "half" | "full";
	renderScale: number;
	devicePixelRatioCap: number;
	starfieldFar: number;
	starfieldMid: number;
	tentaclesPerJellyfish: number;
	predictedOrbitSamples: number;
	antialias: "off" | "msaa" | "msaa-fxaa";
}

const profiles: Record<QualityLevel, QualityProfile> = {
	low: {
		bloom: "off",
		renderScale: 0.66,
		devicePixelRatioCap: 1.5,
		starfieldFar: 1000,
		starfieldMid: 150,
		tentaclesPerJellyfish: 4,
		predictedOrbitSamples: 64,
		antialias: "off",
	},
	medium: {
		bloom: "half",
		renderScale: 0.85,
		devicePixelRatioCap: 2,
		starfieldFar: 2000,
		starfieldMid: 300,
		tentaclesPerJellyfish: 6,
		predictedOrbitSamples: 96,
		antialias: "msaa",
	},
	high: {
		bloom: "full",
		renderScale: 1,
		devicePixelRatioCap: 2,
		starfieldFar: 3000,
		starfieldMid: 500,
		tentaclesPerJellyfish: 8,
		predictedOrbitSamples: 128,
		antialias: "msaa-fxaa",
	},
};

/** Resolve a chosen quality level into its concrete profile. */
export function resolveQualityProfile(level: QualityLevel): QualityProfile {
	return profiles[level];
}

/** Suggest a quality level from device hints. Mobile-primary devices with
 *  what looks like an integrated GPU default to `medium`; everything else
 *  defaults to `high`. The runtime benchmark below is what actually pushes
 *  to `low` when the device can't sustain `medium`. */
export function detectQualityLevel(): QualityLevel {
	if (isTouchPrimary() && isLikelyMobileGpu()) return "medium";
	return "high";
}

export interface BenchmarkSample {
	frameMs: number;
}

/** If a one-frame budget overshoots `slowFrameMs` (default 24 ms = ~42 fps),
 *  drop one quality tier. Returns the (possibly demoted) level. Pure logic;
 *  the actual frame measurement is the renderer's job. */
export function demoteIfSlow(
	level: QualityLevel,
	sample: BenchmarkSample,
	slowFrameMs = 24,
): QualityLevel {
	if (sample.frameMs <= slowFrameMs) return level;
	if (level === "high") return "medium";
	if (level === "medium") return "low";
	return "low";
}
